import { createContext, useContext } from 'react';
import { ScaleLinear } from 'd3-scale';
import { MARGIN, STUNDEN_MAX } from '../constants';
import { scaleLinear } from '@visx/scale';
import { Arbeitsbereich } from '@/domain/dienstplan/shared/types/Arbeitsbereich';
import { MITARBEITER_THRESHOLD } from '@/domain/dienstplan/shared/utils/constants';

type VisualEditorContextProps = {
  xScalePoint: ScaleLinear<number, number, never>;
  yScalePoint: ScaleLinear<number, number, never>;
  yScaleAxis: ScaleLinear<number, number, never>;
  xScaleAxis: ScaleLinear<number, number, never>;
  yMax: number;
  xMax: number;
  width: number;
  height: number;
  selectedArbeitsbereich: Arbeitsbereich | null;
  maxMitarbeiterAnzahl: {
    yAxisValue: number;
    value: number;
  };
};

// eslint-disable-next-line react-refresh/only-export-components
export const VisualEditorContext = createContext<
  VisualEditorContextProps | undefined
>(undefined);

function VisualEditorContextProvider({
  children,
  width,
  height,
  selectedArbeitsbereich,
  maxMitarbeiterAnzahl,
}: {
  children: React.ReactNode;
  width: number;
  height: number;
  selectedArbeitsbereich: Arbeitsbereich | null;
  maxMitarbeiterAnzahl: number;
}) {
  const xMax = width - MARGIN.left - MARGIN.right;
  const yMax = height - MARGIN.top - MARGIN.bottom;

  const xScalePoint = scaleLinear({
    domain: [0, STUNDEN_MAX],
    range: [MARGIN.left, width - MARGIN.right],
  });

  const count = maxMitarbeiterAnzahl < 10 ? 10 : maxMitarbeiterAnzahl;

  const yScalePoint = scaleLinear({
    domain: [0, count + MITARBEITER_THRESHOLD],
    range: [height - MARGIN.bottom, MARGIN.top],
  });

  const yScaleAxis = scaleLinear({
    domain: [0, count + MITARBEITER_THRESHOLD],
    range: [yMax, 0],
  });

  const xScaleAxis = scaleLinear({
    domain: [0, STUNDEN_MAX],
    range: [0, xMax],
  });

  const value: VisualEditorContextProps = {
    yScaleAxis,
    xScaleAxis,
    yScalePoint,
    xScalePoint,
    yMax,
    xMax,
    width,
    height,
    selectedArbeitsbereich,
    maxMitarbeiterAnzahl: {
      yAxisValue: count,
      value: maxMitarbeiterAnzahl,
    },
  };

  return (
    <VisualEditorContext.Provider value={value}>
      {children}
    </VisualEditorContext.Provider>
  );
}

function useVisualEditorContext() {
  const visualEditorContext = useContext(VisualEditorContext);

  if (visualEditorContext === undefined) {
    throw new Error(
      'useVisualEditorContext must be used within VisualEditorContextProvider',
    );
  }
  return visualEditorContext;
}

// eslint-disable-next-line react-refresh/only-export-components
export { useVisualEditorContext, VisualEditorContextProvider };
